import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import '../onboarding/onboarding.css';

interface FeedItem {
  time: string;
  msg: string;
  type: string;
}

export interface TerminalStat {
  label: string;
  value: number;
}

interface TerminalFeedProps {
  messages: FeedItem[];
  isComplete?: boolean;
  /** Live tallies shown along the bottom. Four fit the strip cleanly. */
  stats?: TerminalStat[];
  /** Engine identity line, e.g. "vib3-engine · claude". Cosmetic. */
  model?: string;
}

const FEED_TYPE_COLORS: Record<string, string> = {
  highlight: 'text-[#22C55E]',
  warn: 'text-[#FB923C]',
  finding: 'text-[#60A5FA]',
  alert: 'text-[#F87171]',
};

const MAX_VISIBLE = 100;

/**
 * The live analysis log.
 *
 * Stays dark in both themes on purpose — it's a console, and the light-mode
 * version of this panel read as a rendering bug rather than a choice.
 */
export default function TerminalFeed({ messages, isComplete, stats, model }: TerminalFeedProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Only render the last N messages to keep DOM size bounded
  const visible = messages.length > MAX_VISIBLE ? messages.slice(-MAX_VISIBLE) : messages;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visible.length]);

  return (
    <div className="ob-term flex h-full min-h-0 flex-col rounded-[18px]">
      <div className="relative z-10 flex items-center gap-3 border-b border-[#18181b] bg-black/25 px-5 py-3.5">
        <div className="flex flex-1 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.1em] text-[#A1A1AA]">
          <span className="ob-live inline-flex items-center gap-1.5 font-semibold text-[#22C55E]">
            Live
          </span>
          <span>· Analysis Engine</span>
        </div>
        {model && (
          <div className="font-mono text-[10px] tracking-[0.06em] text-[#52525B]">{model}</div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="ob-term__body relative z-10 min-h-0 flex-1 overflow-y-auto px-5 pb-2.5 pt-4 font-mono text-[12.5px] leading-[1.65]"
        style={{ contain: 'content' }}
      >
        {visible.map((m, i) => (
          <div className="ob-term__line flex gap-2.5 py-0.5" key={i}>
            <span className="shrink-0 whitespace-nowrap tabular-nums text-[#52525B]">{m.time}</span>
            <span className={cn('min-w-0 whitespace-pre-wrap text-[#D4D4D8]', FEED_TYPE_COLORS[m.type])}>
              {m.msg}
            </span>
          </div>
        ))}
        {!isComplete && (
          <div className="flex gap-2.5 py-0.5">
            <span className="shrink-0 whitespace-nowrap text-[#52525B]">[...]</span>
            <span className="inline-flex items-center gap-1.5 text-[#71717A]">
              thinking
              <span className="inline-flex gap-[3px]">
                <i className="ob-blip" />
                <i className="ob-blip" />
                <i className="ob-blip" />
              </span>
              <span className="ob-caret" />
            </span>
          </div>
        )}
      </div>

      {stats && stats.length > 0 && (
        <div
          className="relative z-10 grid gap-3 border-t border-dashed border-[#27272A] bg-black/35 px-5 py-3.5 font-mono"
          style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}
        >
          {stats.map((stat) => (
            <div key={stat.label}>
              <div className="mb-1 text-[9.5px] uppercase tracking-[0.1em] text-[#52525B]">
                {stat.label}
              </div>
              <div className="text-sm font-semibold tabular-nums text-[#FAFAFA]">
                {stat.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
